<template>
  <div v-if="!$apollo.loading">
    <index-values-chart :monthlyIndexValues="monthlyIndexValues" />
    <index-values-table
      :monthlyIndexValues="monthlyIndexValues"
      @valueClicked="showValueDetails($event)"
      @addValueButtonClicked="onAddValueButtonClicked($event)"
      @unpublishButtonClicked="onUnpublishButtonClicked($event)"
    />
    <a-drawer
      :title="detailsDrawerTitle"
      placement="right"
      :visible="showDetailsDrawer"
      @close="closeDetailsDrawer()"
      width="700"
    >
      <index-value-details :indexValueDetails="selectedIndexValueDetails" />
    </a-drawer>
    <a-drawer
      title="Missing Values"
      placement="right"
      :visible="showMissingValuesDrawer"
      @close="closeMissingValuesDrawer()"
      width="600"
    >
      <missing-values
        :constituentsWithMissingValues="constituentsWithMissingValues"
      />
    </a-drawer>
    <a-drawer
      :title="computedValueDrawerTitle"
      placement="right"
      :visible="showComputedValueDrawer"
      @close="closeComputedValueDrawer()"
      width="700"
    >
      <computed-index-value
        :indexValueDetails="computedIndexValueDetails"
        @publishValue="publishValue($event)"
      />
    </a-drawer>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import ComputedIndexValue from './ComputedIndexValue'
import IndexValuesChart from './IndexValuesChart.vue'
import IndexValuesTable from './IndexValuesTable.vue'
import IndexValueDetails from './IndexValueDetails.vue'
import MissingValues from './MissingValues.vue'

export default {
  components: {
    ComputedIndexValue,
    IndexValuesChart,
    IndexValuesTable,
    IndexValueDetails,
    MissingValues,
  },
  props: {
    index: {
      type: String,
      required: true,
    },
  },
  apollo: {
    monthlyIndexValues: {
      query: gql`
        query GetMonthlyIndexValues($indexId: String!) {
          monthlyIndexValues(index: $indexId) {
            value
            periodVariation
            return
            cumulatedReturn
            ytdVolatility
            cumulatedVolatility
            year
            month
          }
        }
      `,
      variables() {
        return {
          indexId: this.index,
        }
      },
    },
  },
  computed: {
    detailsDrawerTitle() {
      if (!this.selectedIndexValueDetails) {
        return ''
      }
      return `${this.selectedIndexValueDetails.month} / ${this.selectedIndexValueDetails.year}`
    },
    computedValueDrawerTitle() {
      if (!this.computedIndexValueDetails) {
        return ''
      }
      return `${this.computedIndexValueDetails.month} / ${this.computedIndexValueDetails.year}`
    },
  },
  data() {
    return {
      monthlyIndexValues: [],
      selectedIndexValueDetails: {},
      showDetailsDrawer: false,
      constituentsWithMissingValues: [],
      showMissingValuesDrawer: false,
      computedIndexValueDetails: {},
      showComputedValueDrawer: false,
    }
  },
  methods: {
    closeDetailsDrawer() {
      this.showDetailsDrawer = false
    },
    closeMissingValuesDrawer() {
      this.showMissingValuesDrawer = false
    },
    closeComputedValueDrawer() {
      this.showComputedValueDrawer = false
    },
    async showValueDetails({ year, month }) {
      const response = await this.$apollo.query({
        query: gql`
          query GetSingleMonthlyIndexValue(
            $indexId: String!
            $year: Int!
            $month: Int!
          ) {
            singleMonthlyIndexValue(
              index: $indexId
              year: $year
              month: $month
            ) {
              year
              month
              value
              periodVariation
              standardDeviation
              ytdStandardDeviation
              cumulatedVolatility
              ytdVolatility
              return
              cumulatedReturn
              constituents {
                constituent
                weight
                shareValue
                dividend
                periodVariation
              }
            }
          }
        `,
        variables: {
          indexId: this.index,
          year,
          month,
        },
      })
      this.selectedIndexValueDetails = response.data.singleMonthlyIndexValue
      this.showDetailsDrawer = true
    },
    async onAddValueButtonClicked({ year, month }) {
      const check = await this.checkCanCompute(year, month)
      if (check.canCompute) {
        const computedValue = await this.computeValue(year, month)
        this.computedIndexValueDetails = computedValue
        this.showComputedValueDrawer = true
      } else {
        this.constituentsWithMissingValues = check.constituentsWithMissingValues
        this.showMissingValuesDrawer = true
      }
    },
    async checkCanCompute(year, month) {
      const response = await this.$apollo.query({
        query: gql`
          query CanCompute($indexId: String!, $year: Int!, $month: Int!) {
            checkCanComputeIndexValue(
              index: $indexId
              year: $year
              month: $month
            ) {
              constituentsWithMissingValues {
                key
                stringValue
              }
              canCompute
            }
          }
        `,
        variables: {
          indexId: this.index,
          year,
          month,
        },
        fetchPolicy: 'no-cache',
      })

      return response.data.checkCanComputeIndexValue
    },
    async computeValue(year, month) {
      const response = await this.$apollo.query({
        query: gql`
          query Compute($indexId: String!, $year: Int!, $month: Int!) {
            computedMonthlyIndexValue(
              index: $indexId
              year: $year
              month: $month
            ) {
              year
              month
              value
              periodVariation
              standardDeviation
              ytdStandardDeviation
              cumulatedVolatility
              ytdVolatility
              return
              cumulatedReturn
              constituents {
                constituent
                shareValue
                dividend
                periodVariation
              }
            }
          }
        `,
        variables: {
          indexId: this.index,
          year,
          month,
        },
      })
      return response.data.computedMonthlyIndexValue
    },
    async publishValue({ year, month }) {
      this.showComputedValueDrawer = false
      const response = await this.$apollo.mutate({
        mutation: gql`
          mutation($input: PublishIndexValueInput) {
            publishIndexValue(input: $input) {
              value
              periodVariation
              return
              cumulatedReturn
              ytdVolatility
              cumulatedVolatility
              year
              month
            }
          }
        `,
        variables: {
          input: {
            index: this.index,
            year: year,
            month: month,
          },
        },
      })
      const indexValue = response.data.publishIndexValue
      this.monthlyIndexValues.push(indexValue)
    },
    async onUnpublishButtonClicked({ year, month }) {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($input: UnpublishIndexValueInput) {
            unpublishIndexValue(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            index: this.index,
            year: year,
            month: month,
          },
        },
      })
      const removedIndexValue = this.monthlyIndexValues.find(
        (v) => v.year === year && v.month === month,
      )
      const removedArrayIndex = this.monthlyIndexValues.indexOf(
        removedIndexValue,
      )
      this.monthlyIndexValues.splice(removedArrayIndex, 1)
    },
  },
}
</script>

<style></style>
